// src/components/ReporterBadge.jsx
// Public-facing reporter trust badge.
//
// Shows what type of reporter submitted the post.
// NEVER shows the internal trust score.
//
// Three public-facing tiers (from trust.js):
//   trusted   — trusted_community (0.65+)  "Trusted Reporter"
//   verified  — phone-verified, good history (0.40+)  "Verified Reporter"
//   new       — new account (0.20+)  "New Reporter"
//   anonymous — unverifiable  "Community Report"
//   official  — machine/agency source  "Official Source"
//
// Shown inline with post.auth in FeedCard.
// Clicking a badge shows a tooltip explaining what the tier means.

import { useState } from 'react';
import { T }        from '@components/theme';

const TIERS = {
  // ── Trusted Verifier Program ──────────────────────────────────────
  trusted_verifier: {
    label:    'Trusted Verifier',
    short:    'Verifier',
    color:    '#059669',
    icon:     '🔰',
    desc:     'Community Emergency Response Team (CERT) member, licensed ham operator, or NWS-trained storm spotter. Admin-verified identity.',
  },
  cert_member: {
    label:    'CERT Member',
    short:    'CERT',
    color:    '#059669',
    icon:     '🔰',
    desc:     'Trained Community Emergency Response Team member. Verified by local emergency management.',
  },
  ham_operator: {
    label:    'Ham Operator',
    short:    'HAM',
    color:    '#0369a1',
    icon:     '📻',
    desc:     'Licensed amateur radio operator. FCC-verified identity. Active in Louisiana ARES/RACES.',
  },
  volunteer_spotter: {
    label:    'Volunteer Spotter',
    short:    'Spotter',
    color:    '#0369a1',
    icon:     '🌪',
    desc:     'NWS-trained storm spotter. Volunteer trained in structured weather observation.',
  },
  community_leader: {
    label:    'Community Leader',
    short:    'Leader',
    color:    '#7c3aed',
    icon:     '🏘',
    desc:     'Verified community leader with established identity in their neighborhood.',
  },
  // ── Standard tiers ────────────────────────────────────────────────
  trusted: {
    label:    'Trusted Reporter',
    short:    'Trusted',
    color:    '#059669',
    icon:     '⭐',
    desc:     'This reporter has a strong track record of accurate reports confirmed by official sources.',
  },
  verified: {
    label:    'Verified Reporter',
    short:    'Verified',
    color:    '#0369a1',
    icon:     '✓',
    desc:     'This reporter has a verified phone number and accurate reporting history.',
  },
  new: {
    label:    'New Reporter',
    short:    'New',
    color:    '#6b7280',
    icon:     '○',
    desc:     'This reporter is new to Eye on Storm. Verify with other sources.',
  },
  anonymous: {
    label:    'Community Report',
    short:    'Community',
    color:    '#6b7280',
    icon:     '○',
    desc:     'Submitted anonymously. Treat with caution and verify independently.',
  },
  official: {
    label:    'Official Source',
    short:    'Official',
    color:    '#059669',
    icon:     '📡',
    desc:     'Data from an official agency: NWS, USGS, DOTD, or similar.',
  },
};

export function ReporterBadge({ tier, compact = false }) {
  const [showTip, setShowTip] = useState(false);
  const config = TIERS[tier] ?? TIERS.anonymous;

  return (
    <span style={{ position: 'relative', display: 'inline-block' }}>
      <button
        onClick={(e) => { e.stopPropagation(); setShowTip(v => !v); }}
        title={config.desc}
        aria-label={`Reporter status: ${config.label}`}
        style={{
          display:     'inline-flex',
          alignItems:  'center',
          gap:          3,
          background:  `${config.color}14`,
          border:      `1px solid ${config.color}30`,
          borderRadius: 4,
          padding:     '1px 5px',
          cursor:      'pointer',
          fontFamily:   T.font,
        }}
      >
        <span style={{ fontSize: compact ? 8 : 9 }}>{config.icon}</span>
        <span style={{
          fontSize:   compact ? 7 : 8,
          fontWeight:  700,
          color:       config.color,
          whiteSpace: 'nowrap',
        }}>
          {compact ? config.short : config.label}
        </span>
      </button>

      {showTip && (
        <div
          onClick={(e) => e.stopPropagation()}
          style={{
            position:    'absolute',
            bottom:      'calc(100% + 6px)',
            left:         0,
            zIndex:       200,
            background:  '#1c1917',
            border:      `1px solid ${config.color}40`,
            borderRadius: 8,
            padding:     '8px 10px',
            width:        220,
            boxShadow:   '0 4px 16px rgba(0,0,0,0.5)',
          }}
        >
          <div style={{ fontSize: 11, fontWeight: 800, color: config.color, marginBottom: 4 }}>
            {config.icon} {config.label}
          </div>
          <div style={{ fontSize: 10, color: '#9ca3af', lineHeight: 1.5 }}>
            {config.desc}
          </div>
          <button
            onClick={() => setShowTip(false)}
            style={{ position:'absolute', top:4, right:6, background:'none',
                     border:'none', color:'#6b7280', cursor:'pointer',
                     fontSize:14, fontFamily:T.font }}
          >×</button>
        </div>
      )}
    </span>
  );
}
